import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, ChevronDown } from "lucide-react";
import { ActivityEventIcon } from "@photon/chat-react/activity-timeline";
import ToolCallDetails from "./ToolCallDetails";
import { getActivityTitle, getDelegationDetails, getItemStyles } from "./activityItemUtils";

/**
 * CompactActivityRow — shared compact activity item renderer.
 *
 * Used by both NebulaActivityPanel (sidebar) and TimelineItem (chat) during streaming
 * and as the post-streaming collapsed/accordion row.
 *
 * Layout: dot column + connector line + content column (flex row).
 * Note: when rendered inside TimelineContainer, set showConnector=false —
 *       the container draws the vertical line itself.
 *
 * Props:
 *   item          — activity item data
 *   isActive      — this is the currently active (last) step during streaming
 *   isStreaming   — whether the parent message is still generating
 *   expanded      — whether the accordion card is open (post-streaming)
 *   onToggle      — callback to open/close accordion
 *   isLast        — suppress the dot-to-dot connector line (last item in a standalone list)
 *   showConnector — set false when inside TimelineContainer which draws its own line
 */
const CompactActivityRow = ({
  item,
  isActive = false,
  isStreaming = false,
  expanded = false,
  onToggle,
  isLast = false,
  showConnector = true,
}) => {
  const styles = getItemStyles(item);
  const isRunningTool = item?.type === "tool" && item?.status === "running";

  const hasDescription =
    item?.type === "tool"
      ? !!(item?.args || item?.code || item?.stdout || item?.stderr || item?.size_hint || item?.description)
      : item?.type === "delegation"
        ? getDelegationDetails(item).length > 0
      : !!(item?.description?.trim());

  // Single rule: caret is shown whenever there's a body to expand. The parent
  // (NebulaActivityPanel / ShowResearch) controls which item is expanded by default
  // via `expandedIndex`. No streaming-specific branching here.
  const showCaret = hasDescription;

  return (
    <motion.div
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="flex gap-2.5"
    >
      {/* Dot + connector line column */}
      {showConnector && (
        <div className="flex flex-col items-center w-4 flex-shrink-0 pt-[5px]">
          <span
            className={`photon-activity-event-icon flex-shrink-0 ${isActive || isRunningTool ? 'animate-pulse' : ''}`}
            data-status={item?.type === 'error' ? 'error' : item?.status}
          >
            <ActivityEventIcon type={item?.type} />
          </span>
          {!isLast && (
            <div className="flex-1 w-px bg-fill-gray-200 dark:bg-fill-panel-surface mt-1.5 min-h-[8px]" />
          )}
        </div>
      )}

      {/* Content column */}
      <div className={`flex-1 min-w-0 ${isLast || !showConnector ? "pb-0" : "pb-1"}`}>
        {/* Title row — clickable when there's a body to expand. Dimmed only for
            non-active items during streaming (visual hierarchy of "current step"). */}
        <div
          className={`flex items-start gap-1 py-[3px] rounded transition-colors ${
            showCaret ? "cursor-pointer hover:bg-fill-gray-100 dark:hover:bg-fill-sidebar-hover-dark" : ""
          } ${!isActive && isStreaming && !expanded ? "opacity-60" : ""}`}
          onClick={showCaret ? onToggle : undefined}
        >
          <span
            className={`photon-activity-title flex-1 min-w-0 text-[10.5px] uppercase font-bold leading-tight tracking-wide break-words ${styles.title}`}
          >
            {getActivityTitle(item)}
          </span>
          {showCaret && (
            <span className="flex-shrink-0 mt-[2px] text-ink-gray-400 dark:text-ink-gray-500">
              {expanded ? (
                <ChevronDown size={9} strokeWidth={2.5} />
              ) : (
                <ChevronRight size={9} strokeWidth={2.5} />
              )}
            </span>
          )}
        </div>

        {/* Body — single rendering path, shown iff `expanded`. The pulse styling
            only applies for the actively-running item to signal liveness. */}
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{
                height: { duration: 0.18, ease: "easeInOut" },
                opacity: { duration: 0.12 },
              }}
              className="overflow-hidden mb-2 mt-0.5"
            >
              {item?.type === "tool" ? (
                <div className="border border-edge-border dark:border-edge-hex-3d3c3a bg-fill-hex-f0f4f9 dark:bg-fill-hex-222222 rounded-[8px] overflow-hidden px-2.5 py-2">
                  <ToolCallDetails
                    code={item?.code}
                    args={item?.args}
                    hint={item?.description}
                    size_hint={item?.size_hint}
                    stdout={item?.stdout}
                    stderr={item?.stderr}
                    isRunning={isRunningTool}
                    cancellationLimitation={item?.cancellationLimitation || item?.cancellation_limitation}
                  />
                </div>
              ) : item?.type === "delegation" ? (
                <div className="space-y-2 border border-edge-border dark:border-edge-hex-3d3c3a px-2.5 py-2 bg-fill-hex-f0f4f9 dark:bg-fill-hex-222222 rounded-[8px]">
                  {getDelegationDetails(item).map(({ label, value }) => (
                    <div key={label}>
                      <p className="text-[9px] font-semibold uppercase text-ink-gray-500 dark:text-ink-gray-400">{label}</p>
                      <p className="mt-0.5 whitespace-pre-line text-[11px] leading-relaxed text-ink-hex-456175 dark:text-ink-hex-cccccc">{value}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border border-edge-border dark:border-edge-hex-3d3c3a px-2.5 py-2 bg-fill-hex-f0f4f9 dark:bg-fill-hex-222222 rounded-[8px]">
                  <p className={`text-[11px] text-ink-hex-456175 dark:text-ink-hex-cccccc leading-relaxed whitespace-pre-line ${isActive && isStreaming ? "animate-pulse line-clamp-4" : ""}`}>
                    {item?.description?.trim() || item?.title?.trim() || "Processing…"}
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default CompactActivityRow;
